import { useState } from 'react';
import { ExternalLink, Lock, Pencil } from 'lucide-react';
import clsx from 'clsx';
import { useGuardarServicio, useServicios, type ServicioWeb } from '../api/servicios';
import { AyudaGuia, Pregunta } from '../components/Ayuda';
import { config } from '../lib/config';

// La web de la agencia, sin desplegar nada: encender o apagar cada servicio,
// pedir cuenta o no, y cambiar el nombre y la descripción que se enseñan. La
// web lo aplica al abrirse (lee /public/services); qué servicios van en cada
// pack sigue en el código de la web.

const SECCIONES: { segmento: ServicioWeb['segmento']; titulo: string; pagina: string }[] = [
  { segmento: 'propietario', titulo: 'Propietario', pagina: '/soy-propietario' },
  { segmento: 'inquilino', titulo: 'Inquilino', pagina: '/soy-inquilino' },
  { segmento: 'comprador', titulo: 'Comprador', pagina: '/soy-comprador' },
  { segmento: 'inversor', titulo: 'Inversor', pagina: '/soy-inversor' },
];
const WEB = config.webUrl ?? config.mediaBase ?? '';

export default function ServiciosView() {
  const { data, isLoading, isError, refetch } = useServicios();

  return (
    <div className="pb-16 md:pb-0">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-extrabold tracking-tight text-deep">Servicios</h1>
        <AyudaGuia seccion="servicios" />
      </div>
      <p className="mt-1 max-w-2xl text-sm text-ink/55">
        Los servicios que ofrece la web. Lo que cambies aquí se ve en la web al recargar la página,
        sin esperar a nadie.
      </p>
      <Pregunta titulo="¿Qué se puede cambiar aquí y qué no?">
        Puedes quitar un servicio de la web (y volver a ponerlo), decidir si hace falta cuenta para
        pedirlo, y cambiar su nombre y su descripción. Lo que no se cambia aquí es qué servicios van
        en cada pack ni añadir servicios nuevos: eso se lo pides a Adrien.
      </Pregunta>

      {isLoading ? (
        <p className="mt-10 text-sm text-ink/50">Cargando…</p>
      ) : isError || !data ? (
        <div className="mt-8 text-center">
          <p className="text-sm font-bold text-red-700">No se han podido cargar los servicios</p>
          <button onClick={() => refetch()} className="mt-3 rounded-full bg-red-600 px-4 py-1.5 text-sm font-bold text-white">Reintentar</button>
        </div>
      ) : (
        <div className="mt-6 space-y-8">
          {SECCIONES.map(({ segmento, titulo, pagina }) => {
            const lista = data
              .filter((s) => s.segmento === segmento)
              .sort((a, b) => (a.web_nombre || a.nombre).localeCompare(b.web_nombre || b.nombre, 'es'));
            if (!lista.length) return null;
            return (
              <section key={segmento}>
                <div className="flex items-baseline justify-between border-b border-deep/10 pb-2">
                  <h2 className="text-sm font-bold uppercase tracking-wider text-ink/55">
                    {titulo} · {lista.filter((s) => s.activo).length} de {lista.length} en la web
                  </h2>
                  <a href={`${WEB}${pagina}`} target="_blank" rel="noopener" className="flex items-center gap-1 text-xs font-bold text-brand hover:text-deep">
                    Ver en la web <ExternalLink size={12} />
                  </a>
                </div>
                <ul className="mt-3 space-y-2">
                  {lista.map((s) => <FilaServicio key={s.service_id} s={s} />)}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FilaServicio({ s }: { s: ServicioWeb }) {
  const guardar = useGuardarServicio();
  const [editando, setEditando] = useState(false);
  const [nombre, setNombre] = useState(s.web_nombre ?? '');
  const [desc, setDesc] = useState(s.web_desc ?? '');
  const cambiado = Boolean(s.web_nombre || s.web_desc);
  const error = (guardar.error as { response?: { data?: { message?: string } } } | null)?.response?.data?.message;

  return (
    <li className={clsx('rounded-2xl border bg-surface px-4 py-3', s.activo ? 'border-deep/10' : 'border-dashed border-deep/20 opacity-70')}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1">
          <p className={clsx('text-sm font-bold', s.activo ? 'text-deep' : 'text-ink/50 line-through')}>
            {s.web_nombre || s.nombre}
          </p>
          <p className="text-xs text-ink/50">
            {!s.activo && 'No sale en la web · '}
            {cambiado ? 'Texto cambiado aquí' : 'Texto de la web'}
            {s.primer_uso_libre && ' · Se puede probar una vez sin cuenta'}
          </p>
        </div>
        <Interruptor
          etiqueta="En la web"
          valor={s.activo}
          onChange={(v) => guardar.mutate({ service_id: s.service_id, activo: v })}
        />
        <Interruptor
          etiqueta="Pide cuenta"
          icono={<Lock size={12} />}
          valor={s.requiere_cuenta}
          onChange={(v) => guardar.mutate({ service_id: s.service_id, requiere_cuenta: v })}
        />
        <button onClick={() => setEditando((x) => !x)} className="flex items-center gap-1.5 rounded-full border border-deep/15 px-3 py-1 text-xs font-bold text-ink/65 hover:border-brand hover:text-brand">
          <Pencil size={12} /> Editar texto
        </button>
      </div>

      {editando && (
        <div className="mt-3 space-y-3 rounded-xl bg-soft p-3">
          <label className="block">
            <span className="mb-1 block text-xs font-bold text-ink/50">Nombre en la web</span>
            <input value={nombre} onChange={(e) => setNombre(e.target.value)} maxLength={80} placeholder={s.nombre} className={INPUT} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-bold text-ink/50">Descripción en la web</span>
            <textarea value={desc} onChange={(e) => setDesc(e.target.value)} maxLength={400} rows={3}
              placeholder="Vacío: se queda la descripción que ya tiene la web" className={INPUT} />
          </label>
          <p className="text-xs text-ink/50">
            La descripción que escribas sustituye a la de todas las páginas donde sale este servicio.
          </p>
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex flex-wrap justify-end gap-2">
            {cambiado && (
              <button
                onClick={() => guardar.mutate({ service_id: s.service_id, web_nombre: '', web_desc: '' },
                  { onSuccess: () => { setNombre(''); setDesc(''); setEditando(false); } })}
                className="rounded-full px-4 py-1.5 text-xs font-bold text-ink/55 hover:text-deep"
              >
                Volver al texto de la web
              </button>
            )}
            <button
              onClick={() => guardar.mutate({ service_id: s.service_id, web_nombre: nombre, web_desc: desc },
                { onSuccess: () => setEditando(false) })}
              disabled={guardar.isPending}
              className="rounded-full bg-brand px-4 py-1.5 text-xs font-bold text-white hover:bg-deep disabled:opacity-50"
            >
              Guardar texto
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

function Interruptor({ etiqueta, valor, onChange, icono }: {
  etiqueta: string; valor: boolean; onChange: (v: boolean) => void; icono?: React.ReactNode;
}) {
  return (
    <button
      role="switch"
      aria-checked={valor}
      onClick={() => onChange(!valor)}
      className="flex items-center gap-2 text-xs font-bold text-ink/65"
    >
      <span className={clsx('relative h-5 w-9 rounded-full transition', valor ? 'bg-brand' : 'bg-deep/20')}>
        <span className={clsx('absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition', valor ? 'left-[1.125rem]' : 'left-0.5')} />
      </span>
      <span className="flex items-center gap-1">{icono}{etiqueta}</span>
    </button>
  );
}

const INPUT =
  'w-full rounded-xl border border-deep/15 bg-surface px-3 py-2 text-sm text-deep outline-none placeholder:text-ink/40 focus:border-brand';
